/**
 * PostHog Analytics Service
 *
 * Privacy-safe product analytics. Restack handles firm, billing, and Office
 * metadata, so DOM autocapture and session replay are intentionally disabled.
 *
 * Setup: Set REACT_APP_POSTHOG_KEY in .env
 */

import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

/**
 * Initialize PostHog. Call once at app startup.
 */
export function initPostHog() {
  if (initialized || !POSTHOG_KEY) {
    if (!POSTHOG_KEY) {
      console.warn('[PostHog] No API key set (REACT_APP_POSTHOG_KEY). Analytics disabled.');
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Never record console, chat, auth, billing, or firm-configuration DOM.
    disable_session_recording: true,
    enable_recording_console_log: false,
    autocapture: false,
    // Capture page views automatically on route change
    capture_pageview: false, // We'll handle this manually for SPA routing
    capture_pageleave: false,
    // Defense in depth if recording or autocapture is enabled accidentally in
    // a future configuration change.
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Performance
    loaded: (ph) => {
      // Disable in development
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        // Still capture in dev for testing, but mark it
        ph.register({ environment: 'development' });
      } else {
        ph.register({ environment: 'production' });
      }
    },
  });

  initialized = true;
  console.log('[PostHog] Initialized');
}

/**
 * Identify a user (call after sign-in).
 */
export function identifyUser(user) {
  if (!initialized || !user) return;

  posthog.identify(user.uid, {
    sign_in_method: user.providerData?.[0]?.providerId || 'unknown',
  });
}

/**
 * Reset user identity (call on sign-out).
 */
export function resetUser() {
  if (!initialized) return;
  posthog.reset();
}

/**
 * Track a custom event.
 */
export function capture(eventName, properties = {}) {
  if (!initialized) return;
  posthog.capture(eventName, properties);
}

/**
 * Track a page view (for SPA route changes).
 */
export function capturePageView(path, title) {
  if (!initialized) return;
  posthog.capture('$pageview', {
    $current_url: window.location.origin + path,
    title: title || document.title,
  });
}

/**
 * Set user properties (persistent across events).
 */
export function setUserProperties(properties) {
  if (!initialized) return;
  posthog.people.set(properties);
}

/**
 * Set one-time properties (only set if not already set).
 */
export function setOnceProperties(properties) {
  if (!initialized) return;
  posthog.people.set_once(properties);
}

/**
 * Start a timed event (for measuring duration).
 * Call capture(eventName) later to record the duration.
 */
const timers = {};
export function startTimer(eventName) {
  timers[eventName] = Date.now();
}

export function captureWithDuration(eventName, properties = {}) {
  if (!initialized) return;
  const startTime = timers[eventName];
  const duration = startTime ? Math.round((Date.now() - startTime) / 1000) : null;
  delete timers[eventName];
  posthog.capture(eventName, {
    ...properties,
    ...(duration !== null ? { duration_seconds: duration } : {}),
  });
}

/**
 * Feature flags (if using PostHog feature flags).
 */
export function isFeatureEnabled(flagName) {
  if (!initialized) return false;
  return posthog.isFeatureEnabled(flagName);
}

/**
 * Group analytics (associate user with a company/team).
 */
export function setGroup(groupType, groupKey, properties = {}) {
  if (!initialized) return;
  posthog.group(groupType, groupKey, properties);
}

// Export the raw posthog instance for advanced usage
export { posthog };
